import {Action, Selector, State, StateContext} from '@ngxs/store';
import {catchError, map, tap} from 'rxjs/operators';
import {Document, DocumentListItem} from '../../models/document.model';
import {FormState} from '../../models/form-state.model';
import {DocumentService} from '../../services/document.service';
import {SearchList} from '../../models/search-list.model';
import {NavService} from '../../services/nav.service';
import {Injectable} from '@angular/core';
import {setFormStatus, setFormValidationErrors} from '../../helpers/form.helper';
import {handleFetchListPage} from '../../helpers/search.helper';
import {FormStatus} from '../../enums/file-status.enum';
import {NavItemCodes} from '../../enums/nav-item-codes.enum';
import {DocumentActions} from './document.actions';

interface DocumentStateModel {
    form: FormState;
    list: SearchList<Document>;
    activeDocument: Document;
    fieldList: Document[];
    owuList: DocumentListItem[];
    attachmentsList: DocumentListItem[];
}

const STATE_NAME = 'document';

@State<DocumentStateModel>({
    name: 'document',
    defaults: {
        form: new FormState(),
        list: new SearchList<Document>({
            filters: new FormState()
        }),
        activeDocument: null,
        fieldList: null,
        owuList: null,
        attachmentsList: null,
    }
})

@Injectable()
export class DocumentState {
    constructor(
        private navService: NavService,
        private documentService: DocumentService
    ) {
    }

    @Selector()
    static documentList(state) {
        return state.list;
    }

    @Selector()
    static activeDocument(state) {
        return state.activeDocument;
    }

    @Selector()
    static documentFormErrors(state) {
        return state.form.validationErrors;
    }

    @Selector()
    static documentFieldList(state) {
        return state.fieldList;
    }

    @Selector()
    static owuList(state) {
        return state.owuList;
    }

    @Selector()
    static attachmentsList(state) {
        return state.attachmentsList;
    }

    @Action(DocumentActions.GetListPage)
    getListPage(ctx: StateContext<DocumentStateModel>, action: DocumentActions.GetListPage) {
        return handleFetchListPage(ctx, action.params, (props) => this.documentService.fetchList(props));
    }

    @Action(DocumentActions.Add)
    addDocument(ctx: StateContext<DocumentStateModel>, {document}: DocumentActions.Add) {
        setFormStatus(ctx, FormStatus.PENDING, STATE_NAME);
        return this.documentService.add(document).subscribe(
            () => this.navService.navigateByCode(NavItemCodes.DOCUMENT_MANAGEMENT),
            ({error}) => ctx.dispatch(new DocumentActions.FormFailure(error))
        );
    }

    @Action(DocumentActions.Update)
    updateDocument(ctx: StateContext<DocumentStateModel>, {document}: DocumentActions.Update) {
        setFormStatus(ctx, FormStatus.PENDING, STATE_NAME);
        return this.documentService.update(document).subscribe(
            () => this.navService.navigateByCode(NavItemCodes.DOCUMENT_MANAGEMENT),
            ({error}) => ctx.dispatch(new DocumentActions.FormFailure(error))
        );
    }

    @Action(DocumentActions.Delete)
    deleteDocument(ctx: StateContext<DocumentStateModel>, {document}: DocumentActions.Delete) {
        return this.documentService.delete(document).pipe(
            map(() => ctx.dispatch(new DocumentActions.GetListPage())),
            catchError((error) => ctx.dispatch(new DocumentActions.FormFailure(error)))
        );
    }

    @Action(DocumentActions.FormFailure)
    addDocumentFailure(ctx: StateContext<DocumentStateModel>, {validationErrors = []}: DocumentActions.FormFailure) {
        setFormValidationErrors(ctx, validationErrors, STATE_NAME);
    }

    @Action(DocumentActions.GetById)
    getDocument(ctx: StateContext<DocumentStateModel>, {id}: DocumentActions.GetById) {
        return this.documentService.fetchById(id).pipe(
            tap((document) => {
                ctx.patchState({
                    activeDocument: new Document(document)
                });
            })
        );
    }

    @Action(DocumentActions.FieldList)
    getFieldList(ctx: StateContext<DocumentStateModel>, {paramsList}: DocumentActions.FieldList) {
        return this.documentService.fetchSimpleList(paramsList).pipe(
            tap((items: Document[]) => {
                ctx.patchState({
                    fieldList: items
                });
            })
        );
    }

    @Action(DocumentActions.OwuList)
    owuList(ctx: StateContext<DocumentStateModel>) {
        return this.documentService.getOwuList().pipe(
            tap((owuList: DocumentListItem[]) => {
                ctx.patchState({
                    owuList
                });
            })
        );
    }

    @Action(DocumentActions.AttachmentsList)
    getAttachmentsList(ctx: StateContext<DocumentStateModel>) {
        return this.documentService.getPolicyAttachmentList().pipe(
            tap((attachmentsList: DocumentListItem[]) => {
                ctx.patchState({
                    attachmentsList
                });
            })
        );
    }
}
